import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';

// A machine's down days (the director, 7 Oct 2026; migration 064): one machine
// out on chosen whole days -- a service, a fitter's visit -- while the rest
// run. Opened from the power button on the machine's row. Before anything is
// saved it says what the days mean for the runs already on them, and the
// director chooses: push them past it, or keep them where they are.

const TEAL = '#14b8a6', AMBER = '#f59e0b', RED = '#ef4444', DIM = '#9ca3af', MUTED = '#6b7280', BORDER = '#2a3040';
const ymd = (d) => d.toISOString().slice(0, 10);
const plus = (s, n) => { const d = new Date(s + 'T12:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return ymd(d); };

export default function DowntimeModal({ machine, onClose, onDone }) {
  const name = machine.display_name || machine.name;
  const today = ymd(new Date());
  // A setter reports it (3 Nov 2026), from today; what happens to the runs is a
  // director's call, on the bell.
  const planner = useAuth().can('plan');
  const [blocks, setBlocks] = useState([]);
  const [reasons, setReasons] = useState(['Service', 'Repair', 'Fitter visit', 'Tool trial', 'No operator', 'Other']);
  const [from, setFrom] = useState(planner ? plus(today, 1) : today);
  const [to, setTo] = useState(planner ? plus(today, 1) : today);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [preview, setPreview] = useState(null);
  const [runs, setRuns] = useState('push');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const loadBlocks = useCallback(() => api.get(`/api/v1/downtime?from=${today}`)
    .then(r => { setBlocks((r.data.blocks || []).filter(b => b.machine_id === machine.id)); if (r.data.reasons) setReasons(r.data.reasons); })
    .catch(() => {}), [machine.id, today]);
  useEffect(() => { loadBlocks(); }, [loadBlocks]);
  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);

  // What the days mean for the runs on them, as soon as they are set.
  useEffect(() => {
    setPreview(null); setError('');
    if (!from || !to) return undefined;
    const t = setTimeout(() => {
      api.post('/api/v1/downtime/preview', { machine_id: machine.id, date_from: from, date_to: to })
        .then(r => setPreview(r.data))
        .catch(err => setError(err.response?.data?.error || 'What those days mean could not be worked out.'));
    }, 300);
    return () => clearTimeout(t);
  }, [from, to, machine.id]);

  async function save() {
    setBusy(true); setError('');
    try {
      const { data } = await api.post('/api/v1/downtime', { machine_id: machine.id, date_from: from, date_to: to, reason, note, runs });
      onDone(data);
    } catch (err) {
      setError(err.response?.data?.error || 'That did not save. Try again.');
      setBusy(false);
    }
  }
  async function takeAway(b) {
    setBusy(true); setError('');
    try {
      const { data } = await api.post(`/api/v1/downtime/${b.id}/remove`);
      onDone({ removed: true, says: data.says });
    } catch (err) {
      setError(err.response?.data?.error || 'That did not work. Try again.');
      setBusy(false);
    }
  }

  const hasRuns = preview && preview.runs && preview.runs.length > 0;
  const ready = reason && (reason !== 'Other' || note.trim().length >= 3) && preview && !error;
  const field = { background: '#12161b', border: `1px solid ${BORDER}`, borderRadius: 8, color: '#f0f2f5', padding: '7px 9px', fontSize: 13 };
  const lab = { fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 };
  const choice = (k, title, says) => (
    <label style={{ display: 'flex', gap: 9, alignItems: 'flex-start', padding: '9px 11px', borderRadius: 9, cursor: 'pointer',
                    border: `1px solid ${runs === k ? TEAL : BORDER}`, background: runs === k ? 'rgba(20,184,166,0.08)' : 'transparent' }}>
      <input type="radio" name="dt-runs" checked={runs === k} onChange={() => setRuns(k)} style={{ marginTop: 3 }} />
      <span><b style={{ fontSize: 13 }}>{title}</b><span style={{ display: 'block', fontSize: 12, color: DIM, marginTop: 2 }}>{says}</span></span>
    </label>
  );

  return (
    <div role="dialog" aria-label={`${name}: down days`} onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, fontFamily: 'system-ui' }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: '#1e2329', border: `1px solid ${BORDER}`, borderRadius: 14, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', color: '#f0f2f5' }}>
        <div style={{ padding: '16px 20px 12px', borderBottom: `1px solid ${BORDER}`, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 800 }}><i className="fa-solid fa-power-off" style={{ color: RED, marginRight: 8, fontSize: 13 }} />{name}: down days</div>
            <div style={{ fontSize: 12.5, color: MUTED, marginTop: 3 }}>Whole days this machine cannot run, while the others do. Nothing is planned on them, and every move steps over them.</div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', color: DIM, fontSize: 17, cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ padding: '14px 20px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {blocks.length > 0 && (
            <div>
              <div style={lab}>Down already</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {blocks.map(b => (
                  <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, border: `1px solid ${BORDER}`, background: '#161b22', fontSize: 13 }}>
                    <span style={{ flex: 1 }}><b>{b.span}</b> · {b.reason}{b.note ? `: ${b.note}` : ''}<span style={{ color: MUTED, fontSize: 12 }}>{b.created_by ? ` · ${b.created_by}` : ''}</span></span>
                    <button disabled={busy} onClick={() => takeAway(b)} style={{ background: 'none', border: `1px solid ${BORDER}`, color: DIM, borderRadius: 7, padding: '4px 9px', fontSize: 12, cursor: 'pointer' }}>Take away</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div><div style={lab}>First day</div><input type="date" value={from} min={today} onChange={e => { setFrom(e.target.value); if (e.target.value > to) setTo(e.target.value); }} style={{ ...field, width: '100%', boxSizing: 'border-box' }} /></div>
            <div><div style={lab}>Last day</div><input type="date" value={to} min={from || today} onChange={e => setTo(e.target.value)} style={{ ...field, width: '100%', boxSizing: 'border-box' }} /></div>
          </div>

          <div>
            <div style={lab}>Why</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {reasons.map(r => (
                <button key={r} onClick={() => setReason(r)} aria-pressed={reason === r}
                  style={{ border: `1px solid ${reason === r ? TEAL : BORDER}`, background: reason === r ? 'rgba(20,184,166,0.12)' : 'transparent', color: reason === r ? TEAL : DIM, borderRadius: 8, padding: '5px 11px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>{r}</button>
              ))}
            </div>
            <input value={note} onChange={e => setNote(e.target.value)} maxLength={300}
              placeholder={reason === 'Other' ? 'Why it is down (needed with Other)' : 'A note, e.g. who is coming (optional)'} aria-label="Note"
              style={{ ...field, width: '100%', boxSizing: 'border-box', marginTop: 8 }} />
          </div>

          {error && <div style={{ fontSize: 13, color: AMBER, border: `1px solid ${AMBER}55`, borderRadius: 8, padding: '8px 10px', background: 'rgba(245,158,11,0.06)' }}>{error}</div>}

          {preview && (
            <div>
              <div style={lab}>The runs on those days</div>
              <div style={{ fontSize: 13, color: hasRuns ? '#f0f2f5' : DIM, marginBottom: hasRuns ? 8 : 0 }}>{preview.says}</div>
              {hasRuns && (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 10 }}>
                    {preview.runs.map(r => (
                      <div key={r.plan_id || r.days[0]} style={{ fontSize: 12.5, color: r.fixed ? AMBER : DIM }}>
                        · {r.part}, {r.days.length} day{r.days.length === 1 ? '' : 's'} then{r.fixed ? ' (started: it cannot move)' : ''}
                      </div>
                    ))}
                  </div>
                  {!planner && <div style={{ fontSize: 12.5, color: DIM }}>They stay where they are. The director is told on the bell and decides what happens to them.</div>}
                  {planner && <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                    {choice('push', 'Push them past it', (preview.notes || []).map(n => n.says).join(' ') || 'They start after the last down day; what comes behind moves only as far as it has to.')}
                    {choice('keep', 'Keep them where they are', 'Nothing moves now. The at-risk list will show them on a down day until you move them.')}
                  </div>}
                </>
              )}
            </div>
          )}
        </div>

        <div style={{ padding: '12px 20px', borderTop: `1px solid ${BORDER}`, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={{ background: 'transparent', color: DIM, border: `1px solid ${BORDER}`, borderRadius: 8, padding: '8px 14px', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
          <button disabled={!ready || busy} onClick={save}
            style={{ background: TEAL, color: '#04201d', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: ready && !busy ? 'pointer' : 'not-allowed', opacity: ready && !busy ? 1 : 0.45 }}>
            {busy ? 'Saving…' : planner && hasRuns && runs === 'push' ? 'Save and push the runs' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
